import React, { ReactNode, useState, useEffect } from "react";
import { Tabs, Box, Divider } from "@mantine/core";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { RouteConstants } from "../../../constants/route-constant";
import SimpleCircularIndicator from "../indicator";
import useColors from "../../../theme/useColors";
import alertIconActive from "../../../assets/Alert1.svg";
import alertIconInactive from "../../../assets/Alert2.svg";
import ModuleBackButton from "../ModuleBackButton";

// Interface for the tab items
interface TabItem {
  value: string;
  label: ReactNode;
  route: string;
  component?: ReactNode;
  notificationCount?: number | string | undefined;
  isAlert?: boolean;
}

interface ReusableTabsProps {
  tabs: TabItem[];
  defaultValue?: string;
  variant?: "default" | "outline" | "pills";
  logo: any;
  logo2: any;
  /** Secondary logo beside tabs: left (CDI/CAiRE) or after tabs toward the right (VBC). */
  logo2Placement?: "left" | "right";
  /** Bold label shown next to logo2 when placement is "right". */
  logo2Tagline?: string;
  /** Widen the left logo2 max-width box (e.g. AURICLE wordmark). */
  logo2Wide?: boolean;
  hideTabs?: boolean;
  /** Optional content shown before the Bulwark logo (e.g. CAiRE patient chip). */
  headerAccessory?: ReactNode;
}

export const ReusableTabs: React.FC<ReusableTabsProps> = ({
  tabs,
  defaultValue,
  variant = "pills",
  logo,
  logo2,
  logo2Placement = "left",
  logo2Tagline,
  logo2Wide = false,
  hideTabs = false,
  headerAccessory,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const colors = useColors();
  const navigate = useNavigate();
  const location = useLocation();

  // Match exact path or a nested child (`/messages/123`), not `/cdi` vs `/cdi-alerts`.
  // Prefer the longest matching route when multiple tabs could qualify.
  const activeTab =
    [...tabs]
      .sort((a, b) => b.route.length - a.route.length)
      .find(
        (tab) =>
          location.pathname === tab.route ||
          location.pathname.startsWith(`${tab.route}/`)
      )?.value || defaultValue;

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      setIsMobile(width <= 505);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Responsive sizing based on screen width
  const getResponsiveSize = () => {
    if (screenWidth > 900) {
      return {
        logoSize: "80px", // Original size, no constraint
        tabPadding: "8px 16px",
        fontSize: "0.9rem",
        minHeight: "36px",
        containerPadding: "12px 8px",
        tabsListPadding: "7px",
        gap: "8px",
        borderRadius: "5px",
        alertIconSize: 18,
        iconMarginBottom: 4,
      };
    } else if (screenWidth > 830) {
      return {
        logoSize: "80px", // Original size, no constraint
        tabPadding: "7px 14px",
        fontSize: "0.9rem",
        minHeight: "35px",
        containerPadding: "11px 7px",
        tabsListPadding: "6px",
        gap: "7px",
        borderRadius: "5px",
        alertIconSize: 18,
        iconMarginBottom: 4,
      };
    } else if (screenWidth > 790) {
      return {
        logoSize: "80px", // Keep original size longer
        tabPadding: "6px 12px",
        fontSize: "0.875rem",
        minHeight: "34px",
        containerPadding: "10px 6px",
        tabsListPadding: "6px",
        gap: "6px",
        borderRadius: "4px",
        alertIconSize: 18,
        iconMarginBottom: 4,
      };
    } else if (screenWidth > 720) {
      return {
        logoSize: "80px", // Still keep original size
        tabPadding: "5px 10px", // Reduced tab padding to make tabs break at 690
        fontSize: "0.8rem",
        minHeight: "32px",
        containerPadding: "9px 5px",
        tabsListPadding: "5px",
        gap: "5px",
        borderRadius: "4px",
        alertIconSize: 18,
        iconMarginBottom: 4,
      };
    } else if (screenWidth > 670) {
      return {
        logoSize: "62px", // Start reducing logo but just a little
        tabPadding: "5px 9px",
        fontSize: "0.8rem",
        minHeight: "31px",
        containerPadding: "8px 5px",
        tabsListPadding: "4px",
        gap: "4px",
        borderRadius: "4px",
        alertIconSize: 16,
        iconMarginBottom: 4,
      };
    } else if (screenWidth > 638) {
      return {
        logoSize: "60px", // Gradual reduction
        tabPadding: "4px 8px",
        fontSize: "0.75rem",
        minHeight: "30px",
        containerPadding: "8px 4px",
        tabsListPadding: "4px",
        gap: "4px",
        borderRadius: "4px",
        alertIconSize: 16,
        iconMarginBottom: 4,
      };
    } else if (screenWidth > 575) {
      return {
        logoSize: "50px", // Continue gradual reduction
        tabPadding: "4px 6px",
        fontSize: "0.75rem",
        minHeight: "29px",
        containerPadding: "7px 4px",
        tabsListPadding: "3px",
        gap: "3px",
        borderRadius: "3px",
        alertIconSize: 16,
        iconMarginBottom: 4,
      };
    } else if (screenWidth > 560) {
      return {
        logoSize: "50px", // Still reasonable size
        tabPadding: "3px 6px",
        fontSize: "0.7rem",
        minHeight: "28px",
        containerPadding: "7px 4px",
        tabsListPadding: "3px",
        gap: "3px",
        borderRadius: "3px",
        alertIconSize: 14,
        iconMarginBottom: 2,
      };
    } else if (screenWidth > 530) {
      return {
        logoSize: "45px", // More reasonable reduction
        tabPadding: "3px 5px",
        fontSize: "0.7rem",
        minHeight: "26px",
        containerPadding: "6px 3px",
        tabsListPadding: "2px",
        gap: "2px",
        borderRadius: "2px",
        alertIconSize: 14,
        iconMarginBottom: 2,
      };
    } else {
      return {
        logoSize: "40px", // Final size for very small screens
        tabPadding: "3px 5px",
        fontSize: "0.7rem",
        minHeight: "26px",
        containerPadding: "6px 3px",
        tabsListPadding: "2px",
        gap: "2px",
        borderRadius: "2px",
        alertIconSize: 14,
        iconMarginBottom: 2,
      };
    }
  };

  const responsiveSize = getResponsiveSize();

  const backButtonSize =
    screenWidth > 720 ? 30 : screenWidth > 575 ? 28 : 24;

  const backButton = <ModuleBackButton size={backButtonSize} />;

  const navigateHome = () => navigate(RouteConstants.ROUTE_HOME);

  const handleHomeLogoKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      navigateHome();
    }
  };

  const renderHomeNavLogo = (
    src: string,
    options?: {
      maxWidth?: string;
      maxHeight?: string;
      objectPosition?: string;
    }
  ) => (
    <Box
      component='img'
      src={src}
      role='link'
      tabIndex={0}
      aria-label='Back to module home'
      onClick={navigateHome}
      onKeyDown={handleHomeLogoKeyDown}
      style={{
        flexShrink: 0,
        cursor: "pointer",
        display: "block",
        ...(responsiveSize.logoSize && {
          maxHeight: options?.maxHeight || responsiveSize.logoSize,
          maxWidth: options?.maxWidth || responsiveSize.logoSize,
          width: "auto",
          height: "auto",
          objectFit: "contain",
          objectPosition: options?.objectPosition || "center",
        }),
      }}
    />
  );

  const leftModuleLogoLink =
    logo2Placement === "left"
      ? renderHomeNavLogo(
          logo2,
          logo2Wide
            ? {
                // Same height budget as other module logos; widen the box so
                // wordmarks aren't squeezed by the square maxWidth constraint.
                maxWidth: `calc(${responsiveSize.logoSize} * 1.35)`,
              }
            : undefined
        )
      : null;

  const bulwarkLogoMaxWidth =
    screenWidth > 720 ? "92px" : screenWidth > 575 ? "74px" : "60px";

  const bulwarkLogoMaxHeight =
    screenWidth > 720 ? "44px" : screenWidth > 575 ? "38px" : "30px";

  const bulwarkLogoLink = renderHomeNavLogo(logo, {
    maxWidth: bulwarkLogoMaxWidth,
    maxHeight: bulwarkLogoMaxHeight,
    objectPosition: "right center",
  });

  // Wide RAQ wordmark: height-led so letter size reads like CDI left logos.
  const raqLogoMaxHeight =
    screenWidth > 720 ? "36px" : screenWidth > 575 ? "30px" : "24px";
  const raqLogoMaxWidth =
    screenWidth > 720 ? "130px" : screenWidth > 575 ? "108px" : "88px";
  // Slightly smaller than the RAQ wordmark height.
  const raqTaglineFontSize =
    screenWidth > 720 ? "15px" : screenWidth > 575 ? "13px" : "11px";

  const rightBrandLockup =
    !isMobile && logo2Placement === "right" && logo2 ? (
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: screenWidth > 720 ? "10px" : "8px",
          flexShrink: 0,
          whiteSpace: "nowrap",
          marginRight: screenWidth > 720 ? "150px" : "120px",
        }}
      >
        <Box
          component='img'
          src={logo2}
          role='link'
          tabIndex={0}
          aria-label='Back to module home'
          onClick={navigateHome}
          onKeyDown={handleHomeLogoKeyDown}
          style={{
            flexShrink: 0,
            cursor: "pointer",
            display: "block",
            maxHeight: raqLogoMaxHeight,
            maxWidth: raqLogoMaxWidth,
            width: "auto",
            height: "auto",
            objectFit: "contain",
            objectPosition: "left center",
          }}
        />
        {logo2Tagline ? (
          <Box
            component='span'
            style={{
              fontWeight: 700,
              fontSize: raqTaglineFontSize,
              lineHeight: 1.15,
              color: "#231f20",
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
            }}
          >
            {logo2Tagline}
          </Box>
        ) : null}
      </Box>
    ) : null;

  const headerRow = (
    <Box
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: responsiveSize.gap,
        padding: isMobile
          ? responsiveSize.containerPadding
          : `${responsiveSize.containerPadding} 12px ${responsiveSize.containerPadding} 4px`,
        alignItems: "center",
        overflow: "hidden",
        width: "100%",
        flexWrap: "nowrap",
      }}
    >
      {!isMobile && backButton}

      {!isMobile && leftModuleLogoLink}

      {!hideTabs && tabs.length > 0 && (
        <Box
          className={isMobile ? undefined : "header-tabs-scroll"}
          style={{
            flex: isMobile ? undefined : 1,
            minWidth: 0,
            overflowX: isMobile ? "visible" : "auto",
            overflowY: "hidden",
            display: "flex",
            justifyContent: isMobile ? "center" : "flex-start",
            WebkitOverflowScrolling: "touch",
            alignSelf: isMobile ? "center" : undefined,
            maxWidth: isMobile ? "100%" : undefined,
          }}
        >
          <Tabs.List
            style={{
              padding: `${Math.max(10, parseInt(responsiveSize.tabsListPadding, 10))}px ${responsiveSize.tabsListPadding} ${responsiveSize.tabsListPadding}`,
              borderRadius: responsiveSize.borderRadius,
              border: `1px solid ${colors.gray[3]}`,
              gap: responsiveSize.gap,
              display: "flex",
              justifyContent: isMobile ? "center" : "flex-start",
              flexWrap: isMobile ? "wrap" : "nowrap",
              flexShrink: 0,
              width: isMobile ? "auto" : "max-content",
              maxWidth: isMobile ? "100%" : undefined,
            }}
          >
            {tabs.map((tab) => {
              const hasBadge = Number(tab.notificationCount) > 0;
              const [tabPadY, tabPadX = tabPadY] =
                responsiveSize.tabPadding.split(" ");
              const tabContent = (
                <Tabs.Tab
                  style={{
                    boxShadow: "0 1px 2px gray",
                    // Extra right padding reserves space for the badge inside this tab
                    padding: hasBadge
                      ? `${tabPadY} ${parseInt(tabPadX, 10) + 10}px`
                      : responsiveSize.tabPadding,
                    fontSize: responsiveSize.fontSize,
                    minHeight: responsiveSize.minHeight,
                    maxHeight: "36px",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    position: "relative",
                    zIndex: 1,
                  }}
                  key={tab.value}
                  value={tab.value}
                >
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: tab.isAlert
                        ? `${Math.max(2, parseInt(responsiveSize?.gap ?? "8", 10) - 4)}px`
                        : responsiveSize?.gap,
                    }}
                  >
                    {tab.isAlert && (
                      <Box
                        component='img'
                        src={
                          tab.value === activeTab
                            ? alertIconActive
                            : alertIconInactive
                        }
                        height={responsiveSize?.alertIconSize}
                        width={responsiveSize?.alertIconSize}
                        style={{
                          marginBottom: responsiveSize?.iconMarginBottom,
                        }}
                      />
                    )}
                    {tab.label}
                  </Box>
                </Tabs.Tab>
              );

              return hasBadge ? (
                <SimpleCircularIndicator
                  count={Number(tab.notificationCount)}
                  color={colors.danger[4]}
                  // Keep badge on this tab's corner (not over the next tab)
                  right={2}
                  top={-6}
                  key={tab.value}
                >
                  {tabContent}
                </SimpleCircularIndicator>
              ) : (
                tabContent
              );
            })}
          </Tabs.List>
        </Box>
      )}

      {hideTabs && !isMobile && <Box style={{ flex: 1, minWidth: 0 }} />}

      {rightBrandLockup}

      {!isMobile && headerAccessory ? (
        <Box style={{ flexShrink: 0, marginRight: 8 }}>
          {headerAccessory}
        </Box>
      ) : null}

      {!isMobile && bulwarkLogoLink}
    </Box>
  );

  const contentArea = (
    <Box
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Outlet />
    </Box>
  );

  if (hideTabs || tabs.length === 0) {
    return (
      <>
        {isMobile && (
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: responsiveSize.gap,
              padding: responsiveSize.containerPadding,
              overflow: "hidden",
            }}
          >
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                gap: responsiveSize.gap,
                flexShrink: 0,
                minWidth: 0,
              }}
            >
              {backButton}
              {leftModuleLogoLink}
            </Box>
            <Box style={{ flexShrink: 0, minWidth: 0, marginLeft: "auto" }}>
              {bulwarkLogoLink}
            </Box>
          </Box>
        )}
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden",
          }}
        >
          {headerRow}
          <Divider />
          {contentArea}
        </Box>
      </>
    );
  }

  return (
    <>
      {isMobile && (
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: responsiveSize.gap,
            padding: responsiveSize.containerPadding,
            overflow: "hidden",
          }}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: responsiveSize.gap,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            {backButton}
            {leftModuleLogoLink}
          </Box>
          <Box style={{ flexShrink: 0, minWidth: 0, marginLeft: "auto" }}>
            {bulwarkLogoLink}
          </Box>
        </Box>
      )}
      <Tabs
        color={colors.primary[5]}
        value={activeTab}
        onChange={(val: any) => {
          const selectedTab = tabs.find((tab) => tab.value === val);
          if (selectedTab) {
            navigate(selectedTab.route);
          }
        }}
        variant={variant}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {headerRow}

        <Divider />

        {contentArea}
      </Tabs>
    </>
  );
};

export default ReusableTabs;
